import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faUser } from '@fortawesome/free-solid-svg-icons';
import ScansFolderView from './ScansFolderView';
import DateFilterCalendar from './DateFilterCalendar';
import MultiCheckSelect from './MultiCheckSelect';
import { ClassSectionFilters, SubjectColumn, itemMatchesClass, itemMatchesAssignmentWord, itemsInClass } from './ClassFolderSearch';
import { folderDisplayTitle } from '../utils/folderDisplay';
import { dropClearedRegrade, REGRADE_CLEARED_EVENT } from '../utils/regradeBatchTitle';
import RegradeChip from './RegradeChip';
import { formatMissingImageNumbers } from '../utils/ocrMissing';
import { isSuspectedNote, suspectedPagesFromNotes } from '../utils/ocrNoise';
import MessageBox from './MessageBox';
import QuestionReportSheet from './QuestionReportSheet';
import './ScansTab.css';

const API_BASE_URL = window.API_BASE_URL;

// Same clock the Sort Papers cards use for Last checked and Last approved.
const formatFolderPkt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-PK', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }) + ' PKT';
};

// Mirror of folder-server.js MONTH_SHORT — used to compute the on-disk name
// the watcher would have created for a backend folder, so we can match it
// against the local-folders list and show the purple "in G Drive" badge.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DATE_SUFFIX_RE = / (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}, \d{4}$/i;

function buildLocalFolderName(folder) {
  if (!folder || !folder.test_date) return '';
  const d = new Date(folder.test_date);
  if (isNaN(d.getTime())) return '';
  // Use the DISPLAY title (with " MCQs" suffix for MCQ folders) so the
  // computed on-disk name is unique per folder type. Without this, a
  // subjective folder "1C - ART" and an MCQ folder "1C - ART" produce the
  // same expected path and both light up the badge when only one is
  // actually present locally.
  const title = String(folderDisplayTitle(folder) || '').trim();
  if (!title) return '';
  if (DATE_SUFFIX_RE.test(title)) return title;
  const suffix = `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  return `${title} ${suffix}`;
}

export default function ScansTab({ currentUser }) {
  const [folders, setFolders] = useState([]);
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  // When navigated here from Re-checks (or similar), open this student after
  // the folder loads. Cleared once ScansFolderView consumes it.
  const [autoOpenStudent, setAutoOpenStudent] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Filters
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterSubject, setFilterSubject] = useState([]);
  const [filterClass, setFilterClass] = useState([]);
  // Printed status filter — 'all' (default), 'printed' (only folders with
  // a printed_at timestamp), or 'not-printed' (only folders that haven't
  // been printed yet). Intersects with teacher/subject/date filters.
  const [filterPrinted, setFilterPrinted] = useState('all');
  // Folder kind — subjective by default. 'mcq' and 'all' are the other choices.
  const [filterKind, setFilterKind] = useState('subjective');
  const [filterMode, setFilterMode] = useState('none'); // 'none', 'all', 'today', 'date', or 'future'
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  // Class / section / subject buttons, same as Student Lists. While a class
  // is chosen, the date range steps aside. A calendar click clears these.
  const [gradeFilter, setGradeFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [subjectPick, setSubjectPick] = useState(null);
  // First word of the assignment name. Narrows whichever list is showing.
  const [assignmentWord, setAssignmentWord] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Names of folders the local watcher reports as present on disk. Loaded from
  // the local folder-server at boot + every time the user comes back to this
  // tab; used to render the purple "in G Drive" badge below the teacher name
  // when a backend folder name lines up with a local one. Empty set means
  // either the watcher isn't running or the user hasn't created any local
  // folders yet — we just hide the badge silently.
  const [localFolderNames, setLocalFolderNames] = useState(new Set());
  // Transient toast (success/error/info) for the per-folder Upload button.
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  // Folder ids whose images are currently being uploaded on demand (per-card
  // "Upload" button) — used to show a spinner / disable the button mid-upload.
  const [uploadingFolderIds, setUploadingFolderIds] = useState(() => new Set());
  // Folder ids that finished uploading this session — flips the button green
  // with an "Uploaded" label.
  const [uploadedFolderIds, setUploadedFolderIds] = useState(() => new Set());
  const [reportFolder, setReportFolder] = useState(null);
  // Same sources as the Sort Papers cards: saved check time, auto-check
  // steps, and the sorted-snapshot status used when an approve has no step time.
  const [assessSnapshotMetaMap, setAssessSnapshotMetaMap] = useState(() => new Map());
  const [autoCheckStepsMap, setAutoCheckStepsMap] = useState(() => new Map());
  const [snapshotFolderStatusMap, setSnapshotFolderStatusMap] = useState(() => new Map());
  const [folderReviews, setFolderReviews] = useState({ verified: {}, requests: {} });
  const [unmatchedLabelMap, setUnmatchedLabelMap] = useState(() => new Map());
  const [ocrNoiseMap, setOcrNoiseMap] = useState(() => new Map());
  const [unexpectedBlankMap, setUnexpectedBlankMap] = useState(() => new Map());
  const [overMaxMarksMap, setOverMaxMarksMap] = useState(() => new Map());
  const [thinkingMarksMap, setThinkingMarksMap] = useState(() => new Map());
  const [checkErrorMap, setCheckErrorMap] = useState(() => new Map());
  const [errorHover, setErrorHover] = useState(null);
  const errorHoverTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`http://${window.location.hostname}:3099/local-folders`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const names = Array.isArray(data.folders) ? data.folders : [];
        setLocalFolderNames(new Set(names.map(n => n.toLowerCase())));
      } catch (_) {
        // Watcher isn't running — leave the set empty. No badge will render.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (e) => {
      if (selectedFolder) {
        e.preventDefault();
        setSelectedFolder(null);
        window.history.pushState({ scansTab: true }, '');
      }
    };

    // Push initial state when component mounts
    if (window.history.state?.scansTab !== true) {
      window.history.pushState({ scansTab: true }, '');
    }

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFolder]);
  
  useEffect(() => {
    loadFolders();
    fetchFolderReviews();
  }, []);

  useEffect(() => {
    const onClear = (event) => {
      const detail = (event && event.detail) || {};
      if (!detail.folderId || detail.slot == null) return;
      setFolderReviews((prev) => dropClearedRegrade(prev, detail.folderId, detail.slot));
    };
    window.addEventListener(REGRADE_CLEARED_EVENT, onClear);
    return () => window.removeEventListener(REGRADE_CLEARED_EVENT, onClear);
  }, []);

  // Auto-open a folder when navigated here with state or ?folder= query —
  // e.g. Sort/Analyze pass { autoOpenFolderId }, Re-checks opens a new tab
  // with ?folder=&student=&images=. Match by rubric id so same-named folders
  // on different dates aren't confused. Clear state/query afterwards so a
  // back-nav / refresh doesn't re-trigger it.
  useEffect(() => {
    const search = new URLSearchParams(location.search || '');
    const openId = location.state?.autoOpenFolderId ?? search.get('folder');
    if (openId == null || openId === '' || !folders.length) return;
    const match = folders.find(f => String(f.id) === String(openId));
    if (match) {
      const hint = {
        resultDataId: location.state?.autoOpenResultDataId ?? search.get('resultDataId'),
        studentName: location.state?.autoOpenStudentName || search.get('student') || '',
        studentImages: location.state?.autoOpenStudentImages || search.get('images') || ''
      };
      if (hint.resultDataId != null || hint.studentName || hint.studentImages) {
        setAutoOpenStudent(hint);
      } else {
        setAutoOpenStudent(null);
      }
      setSelectedFolder(match);
      navigate('/scans', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, location.state, location.search]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, filterTeacher, filterSubject, filterClass, filterPrinted, filterKind, filterMode, selectedDate, selectedEndDate, gradeFilter, sectionFilter, subjectPick, assignmentWord]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/scans/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load folders');
      }

      const loaded = data.folders || [];
      setFolders(loaded);
      fetchFolderTimes();
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderTimes = async () => {
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const assessRes = await fetch(`${API_BASE_URL}/assess/snapshots/folders`, { headers });
      if (assessRes.ok) {
        const assessData = await assessRes.json();
        const metaMap = new Map();
        for (const row of (assessData.data || [])) {
          const match = String(row.folder_path || '').match(/rubrics\/(\d+)/);
          if (match) metaMap.set(match[1], { saved_at: row.saved_at || null });
        }
        setAssessSnapshotMetaMap(metaMap);
      }
    } catch (err) {
      console.warn('[ScansTab] Failed to fetch check times:', err);
    }
    try {
      const stepRes = await fetch(`${API_BASE_URL}/analyze/auto-check/steps`, { headers });
      if (stepRes.ok) {
        const stepData = await stepRes.json();
        const stepMap = new Map();
        for (const row of (stepData.data || [])) {
          stepMap.set(String(row.id), {
            run_at: row.run_at || null,
            steps: row.steps || {}
          });
        }
        setAutoCheckStepsMap(stepMap);
      }
    } catch (err) {
      console.warn('[ScansTab] Failed to fetch approve times:', err);
    }
    try {
      const snapRes = await fetch(`${API_BASE_URL}/analyze/snapshots/folders`, { headers });
      if (snapRes.ok) {
        const snapData = await snapRes.json();
        const statusMap = new Map();
        for (const row of (snapData.data || [])) {
          const match = String(row.folder_path || '').match(/rubrics\/(\d+)/);
          if (match) statusMap.set(match[1], row.status || 'saved');
        }
        setSnapshotFolderStatusMap(statusMap);
      }
    } catch (err) {
      console.warn('[ScansTab] Failed to fetch snapshot status:', err);
    }
    const takeMap = async (url, setter) => {
      try {
        const res = await fetch(url, { headers, cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const map = new Map();
        for (const row of (data.data || [])) {
          if (row && row.rubric_id != null) map.set(String(row.rubric_id), row);
        }
        setter(map);
      } catch (err) {
        console.warn('[ScansTab] Failed to fetch card flags:', err);
      }
    };
    await Promise.all([
      takeMap(`${API_BASE_URL}/assess/unmatched-labels`, setUnmatchedLabelMap),
      takeMap(`${API_BASE_URL}/assess/ocr-noise`, setOcrNoiseMap),
      takeMap(`${API_BASE_URL}/assess/unexpected-blank-pages`, setUnexpectedBlankMap),
      takeMap(`${API_BASE_URL}/assess/over-max-marks`, setOverMaxMarksMap),
      takeMap(`${API_BASE_URL}/assess/thinking-in-marks`, setThinkingMarksMap),
      takeMap(`${API_BASE_URL}/assess/check-errors`, setCheckErrorMap)
    ]);
  };

  const showErrorHover = (payload) => {
    if (errorHoverTimer.current) clearTimeout(errorHoverTimer.current);
    setErrorHover(payload);
  };
  const hideErrorHover = () => {
    if (errorHoverTimer.current) clearTimeout(errorHoverTimer.current);
    errorHoverTimer.current = setTimeout(() => setErrorHover(null), 180);
  };

  const fetchFolderReviews = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/folder-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) return;
      setFolderReviews({
        verified: data.verified || {},
        requests: data.requests || {}
      });
    } catch (err) {
      console.error('Error fetching folder reviews:', err);
    }
  };

  const applyFolderReview = (folderId, review) => {
    if (!review) return;
    const id = String(folderId);
    setFolderReviews((prev) => ({
      verified: { ...prev.verified, [id]: !!review.result_ok },
      requests: { ...prev.requests, [id]: Array.isArray(review.requests) ? review.requests : [] }
    }));
  };

  const openRegradeFromCard = (e, folder) => {
    e.stopPropagation();
    const requests = ((folderReviews.requests && folderReviews.requests[String(folder.id)]) || [])
      .slice()
      .sort((a, b) => Number(a.slot) - Number(b.slot));
    if (!requests.length) return;
    const queue = requests.map((request) => ({
      slot: request.slot,
      students: request.students || [],
      questions: request.questions || [],
      note: request.note || '',
      createdBy: request.created_by || '',
      audioUrl: ''
    }));
    navigate('/evaluate/check', {
        state: {
          autoOpenFolder: {
            folder_path: `rubrics/${folder.id}`,
            title: folderDisplayTitle(folder),
            class: folder.class || '',
            subject: folder.subject || '',
            intent: 'regrade-request',
            regradeRequest: queue[0],
            regradeQueue: queue
          }
        }
      });
  };

  // Today's date as YYYY-MM-DD, for the Future Cards window comparison.
  const todayKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  // Folders of the selected kind. Calendar counts and the card list both
  // read this, so a day highlighted on the calendar has folders of that kind.
  const kindFolders = React.useMemo(() => {
    if (filterKind === 'mcq') return (folders || []).filter((f) => f.type === 'mcq');
    if (filterKind === 'subjective') return (folders || []).filter((f) => f.type !== 'mcq');
    return folders || [];
  }, [folders, filterKind]);

  // Folders narrowed by teacher / class / subject / printed. Calendar day
  // counts, Future Cards badge, and the card list all read from this so a
  // tick updates everything the same way Sort Papers does.
  const visibleFolders = React.useMemo(() => {
    return kindFolders.filter((f) => {
      if (filterTeacher && f.created_by !== filterTeacher) return false;
      if (filterSubject.length && !filterSubject.some((item) => item.value === f.subject)) return false;
      if (filterClass.length && !filterClass.some((item) => item.value === (f.class || ''))) return false;
      if (filterPrinted === 'printed' && !f.printed_at) return false;
      if (filterPrinted === 'not-printed' && (f.printed_at || f.type === 'mcq')) return false;
      return true;
    });
  }, [kindFolders, filterTeacher, filterSubject, filterClass, filterPrinted]);

  // Count of folders dated tomorrow onwards — badge on Future Cards.
  const futureFolderCount = React.useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return visibleFolders.filter((f) => {
      if (!f.test_date) return false;
      const d = new Date(f.test_date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() >= tomorrow.getTime();
    }).length;
  }, [visibleFolders]);

  const folderDateKey = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') {
      const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    d.setHours(0, 0, 0, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const applyFilters = () => {
    let filtered = [...visibleFolders];

    // Class search shows every date for that class. The calendar is idle.
    if (gradeFilter) {
      filtered = filtered.filter((folder) => itemMatchesClass(folder, gradeFilter, sectionFilter, subjectPick));
    } else if (filterMode === 'none') {
      filtered = [];
    } else if (filterMode === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(folder => {
        if (!folder.test_date) return false;
        
        const testDate = new Date(folder.test_date);
        testDate.setHours(0, 0, 0, 0);
        
        return testDate.getTime() === today.getTime();
      });
    } else if (filterMode === 'date' && selectedDate) {
      // One day, or every day from start through end — same as Sort Papers.
      const startKey = selectedDate;
      const endKey = selectedEndDate || selectedDate;
      filtered = filtered.filter((folder) => {
        const key = folderDateKey(folder.test_date);
        return key && key >= startKey && key <= endKey;
      });
    } else if (filterMode === 'future') {
      // "Future Cards" — folders dated tomorrow (00:00) or later.
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      filtered = filtered.filter(folder => {
        if (!folder.test_date) return false;

        const testDate = new Date(folder.test_date);
        testDate.setHours(0, 0, 0, 0);

        return testDate.getTime() >= tomorrow.getTime();
      });
    }

    if (assignmentWord) {
      filtered = filtered.filter((folder) => itemMatchesAssignmentWord(folder, assignmentWord));
    }

    // Apply natural sorting by title
    filtered.sort((a, b) => {
      return (a.title || '').localeCompare(b.title || '', undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });

    setFilteredFolders(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    // Push state to browser history so back button works
    window.history.pushState({ scansTab: true, folderId: folder.id }, '');
  };

  // Upload THIS folder's local images to the server, on demand. Asks the local
  // folder-server (port 3099) to scan just this folder on disk and upload any
  // images not already on the server — nothing else is touched. Same behaviour
  // as the Upload button on the Sort/Evaluate tab.
  const uploadFolderImages = async (e, folder) => {
    e.stopPropagation(); // don't also open the folder (card onClick)
    const id = folder.id;
    if (uploadingFolderIds.has(id)) return; // already in flight
    setUploadingFolderIds((prev) => new Set(prev).add(id));
    setMessage({ show: true, text: `Uploading images for ${folderDisplayTitle(folder)}…`, type: 'info' });
    try {
      const LOCAL_SERVER_URL = `http://${window.location.hostname}:3099`;
      const res = await fetch(`${LOCAL_SERVER_URL}/watcher/upload-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folder.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
      }
      const title = folderDisplayTitle(folder);
      if (data.blocked) {
        // Local folder and website are NOT in sync — the folder-server has
        // paused auto-sort for this folder. Surface exactly what's wrong and
        // keep the button as "Upload" (red state) so the user retries.
        const bits = [];
        if (data.failed)   bits.push(`${data.failed} failed to upload`);
        if (data.badNames) bits.push(`${data.badNames} have bad names (must be "Image_<number>.jpg")`);
        if (data.missing)  bits.push(`${data.missing} still missing`);
        setMessage({
          show: true,
          type: 'error',
          text: `⚠️ ${title}: local folder has ${data.localImages} image${data.localImages === 1 ? '' : 's'} but the website has ${data.backendCount}`
            + (bits.length ? ` — ${bits.join(', ')}` : '')
            + `. Sorting and checking are PAUSED for this folder until they match — click Upload again to retry (rename/remove any non "Image_<n>.jpg" files).`
        });
        // Do NOT flip the button green — it isn't done.
      } else {
        setMessage({
          show: true,
          type: 'success',
          text: data.uploaded > 0
            ? `Uploaded ${data.uploaded} image${data.uploaded === 1 ? '' : 's'} for ${title}. Local & website match (${data.backendCount != null ? data.backendCount : data.total}).`
            : `Nothing new to upload for ${title} — all ${data.total || 0} image${data.total === 1 ? '' : 's'} already on the server.`
        });
        setUploadedFolderIds((prev) => new Set(prev).add(id)); // flip button green → "Uploaded"
      }
      // Refresh so the card's image count reflects the new uploads.
      loadFolders();
    } catch (err) {
      setMessage({
        show: true,
        text: `Upload failed: ${err.message}. Is the local folder-server running on port 3099?`,
        type: 'error'
      });
    } finally {
      setUploadingFolderIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
    // Refresh folders to update scan counts and printed status
    loadFolders();
  };

  // Export empty folders to local PC
  const handleExportEmptyFolders = async () => {
    try {
      setExporting(true);
      setExportResult(null);

      // Each folder carries its own test_date so the local export creates it
      // under <Month>\<Day> matching the folder's date — not today's.
      // Title goes through folderDisplayTitle so MCQ folders pick up the
      // " MCQs" suffix on disk (e.g. "4C - s.study-a MCQs"), matching what
      // the UI shows. Subjective folders are unchanged.
      const emptyFolders = filteredFolders
        .filter(f => f.scan_count === 0)
        .map(f => ({
          title: folderDisplayTitle(f).trim(),
          date: f.test_date || null
        }))
        .filter(e => e.title);

      if (emptyFolders.length === 0) {
        setExportResult({ type: 'warning', message: 'No empty folders to export.' });
        return;
      }

      const response = await fetch(`http://${window.location.hostname}:3099/create-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folders: emptyFolders })
      });

      const data = await response.json();

      if (data.success) {
        const locationLabel = (data.paths && data.paths.length > 1)
          ? `${data.paths.length} date folders`
          : (data.path || (data.paths && data.paths[0]) || 'local disk');
        setExportResult({
          type: 'success',
          message: `Created ${data.created.length} folder(s), skipped ${data.skipped.length} duplicate(s). Location: ${locationLabel}`
        });
      } else {
        setExportResult({ type: 'error', message: data.error || 'Failed to create folders' });
      }
    } catch (err) {
      setExportResult({
        type: 'error',
        message: 'Cannot connect to local server. Make sure folder-server is running (node local-server/folder-server.js)'
      });
    } finally {
      setExporting(false);
      setTimeout(() => setExportResult(null), 8000);
    }
  };

  // Get unique teachers, subjects and classes for filters. Class list uses
  // natural sort so "1C" sits before "10C".
  const uniqueTeachers = [...new Set(folders.map(f => f.created_by))].sort();
  const uniqueSubjects = [...new Set(folders.map(f => f.subject))].sort();
  const uniqueClasses = [...new Set(folders.map(f => f.class).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));

  // If a folder is selected, show the folder view
  if (selectedFolder) {
    return (
      <ScansFolderView
        folder={selectedFolder}
        currentUser={currentUser}
        onBack={() => {
          setAutoOpenStudent(null);
          handleBackToFolders();
        }}
        autoOpenStudent={autoOpenStudent}
        onAutoOpenStudentConsumed={() => setAutoOpenStudent(null)}
      />
    );
  }

  return (
    <div className="scans-tab">
      {/* Header — same shape as SavedRubrics: icon chip + title/subtitle on
          the left, inline Teacher / Class / Subject dropdowns on the right
          of the same row. The old standalone .scans-filters card is gone;
          Status (printed/not-printed) + Today's-Tests + Export sit on a
          slim secondary row beneath so the primary row stays uncluttered. */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}>
            <FontAwesomeIcon icon={faFolder} style={{ color: 'white', fontSize: '20px' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{
              color: 'var(--text-primary)',
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: '700'
            }}>Scans Storage ({filteredFolders.length})</h1>
            <p style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: '0.75rem'
            }}>Manage image scans organized by rubrics</p>
          </div>
          {/* Future Cards — top-row toggle: shows every folder dated tomorrow
              onwards, and jumps the calendar to the next 15 days. */}
          <button
            onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode(filterMode === 'future' ? 'none' : 'future'); setSelectedDate(''); setSelectedEndDate(''); }}
            title="Show only folders dated tomorrow onwards"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              background: filterMode === 'future' ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'var(--surface)',
              color: filterMode === 'future' ? 'white' : 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-forward"></i>
            {filterMode === 'future' ? 'Future Cards · On' : 'Future Cards'}
            {futureFolderCount > 0 && (
              <span style={{
                minWidth: 16, height: 16, padding: '0 5px', borderRadius: 8,
                fontSize: 10, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: filterMode === 'future' ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                color: 'white'
              }}>{futureFolderCount}</span>
            )}
          </button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Teacher Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-user-tie" style={{ color: '#8B5CF6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
              <select
                value={filterTeacher}
                onChange={(e) => setFilterTeacher(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                  minWidth: 150,
                  background: 'var(--surface)'
                }}
              >
                <option value="">All Teachers</option>
                {uniqueTeachers.map(teacher => (
                  <option key={teacher} value={teacher}>{teacher}</option>
                ))}
              </select>
            </div>

            {/* Class Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
              <MultiCheckSelect
                value={filterClass}
                onChange={setFilterClass}
                options={uniqueClasses}
                allLabel="All Classes"
                minWidth={140}
              />
            </div>

            {/* Subject Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-book" style={{ color: '#10b981', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
              <MultiCheckSelect
                value={filterSubject}
                onChange={setFilterSubject}
                options={uniqueSubjects}
                allLabel="All Subjects"
                minWidth={160}
              />
            </div>

            {/* All Scans / Today's Tests toggle — kept in the header
                filter row alongside Teacher/Class/Subject. Status segmented
                + Create Folder live BELOW the calendar (see the block just
                after the <DateFilterCalendar /> below). */}
            <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', background: 'var(--tab-bg, rgba(0,0,0,0.04))', padding: 3, borderRadius: 8 }}>
              <button
                onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode('all'); setSelectedDate(''); setSelectedEndDate(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: filterMode === 'all' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: filterMode === 'all' ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                All Scans
              </button>
              <button
                onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode('today'); setSelectedDate(''); setSelectedEndDate(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: filterMode === 'today' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: filterMode === 'today' ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                Today
              </button>
            </div>

            {(filterTeacher || filterSubject.length > 0 || filterClass.length > 0 || filterPrinted !== 'all' || gradeFilter || assignmentWord) && (
              <button
                onClick={() => { setFilterTeacher(''); setFilterSubject([]); setFilterClass([]); setFilterPrinted('all'); setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setAssignmentWord(null); }}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)'
                }}
              >
                <i className="fas fa-filter-circle-xmark"></i>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Export result notification */}
      {exportResult && (
        <div style={{
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 12,
          fontWeight: 600,
          fontSize: 14,
          background: exportResult.type === 'success' ? '#D1FAE5' : exportResult.type === 'warning' ? '#FEF3C7' : '#FEE2E2',
          color: exportResult.type === 'success' ? '#065F46' : exportResult.type === 'warning' ? '#92400E' : '#991B1B',
          border: `1px solid ${exportResult.type === 'success' ? '#6EE7B7' : exportResult.type === 'warning' ? '#FCD34D' : '#FCA5A5'}`
        }}>
          {exportResult.message}
        </div>
      )}

      {/* Error message */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading state */}
      {loading && <div className="loading-message">Loading folders...</div>}

      {/* Two-column layout: compact 3-column calendar in a narrow left rail,
          folder cards filling the right side. Stacks on narrow viewports via
          flexWrap. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ width: 360, flexShrink: 0 }}>
          <DateFilterCalendar
            items={visibleFolders}
            dateField="test_date"
            range
            selectedDate={filterMode === 'date' ? selectedDate : ''}
            selectedEndDate={filterMode === 'date' ? selectedEndDate : ''}
            onDateChange={(startStr, endStr = '') => {
              setGradeFilter(null);
              setSectionFilter(null);
              setSubjectPick(null);
              if (startStr) {
                setSelectedDate(startStr);
                setSelectedEndDate(endStr || '');
                setFilterMode('date');
              } else {
                setSelectedDate('');
                setSelectedEndDate('');
                setFilterMode('none');
              }
            }}
            itemLabelSingular="Folder"
            itemLabelPlural="Folders"
            title="Date range"
            columns={5}
            // Jump to the next-30-days window while "Future Cards" is on (or a
            // future day stays selected), so upcoming folders are pickable.
            future={filterMode === 'future' || (filterMode === 'date' && !!selectedDate && (
              selectedDate > todayKey || (!!selectedEndDate && selectedEndDate > todayKey)
            ))}
          />

          {/* Below-calendar action strip: Create Folder (extreme left),
              then Status filter, All-Scans/Today toggle. Wraps to multiple
              lines if the 360px rail is too narrow for everything. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button
              onClick={handleExportEmptyFolders}
              disabled={exporting}
              title="Create local folders for scans with 0 images"
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: 8,
                cursor: exporting ? 'wait' : 'pointer',
                fontWeight: 600,
                fontSize: 12,
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                opacity: exporting ? 0.7 : 1,
                transition: 'all 0.2s',
                boxShadow: exporting ? 'none' : '0 2px 6px rgba(16, 185, 129, 0.3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap'
              }}
            >
              <i className="fas fa-folder-plus"></i>
              {exporting ? 'Creating…' : 'Create Folder'}
            </button>

            <div style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--surface)'
            }}>
              {[
                { v: 'all',         label: 'All' },
                { v: 'printed',     label: 'Printed' },
                { v: 'not-printed', label: 'Not Printed' }
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setFilterPrinted(opt.v)}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    background: filterPrinted === opt.v
                      ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                      : 'transparent',
                    color: filterPrinted === opt.v ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

          </div>

          <div style={{
            display: 'flex',
            marginTop: 8,
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--surface)'
          }}>
            {[
              { v: 'subjective', label: 'Subjective' },
              { v: 'mcq', label: 'MCQs' },
              { v: 'all', label: 'All' }
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setFilterKind(opt.v)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: 'none',
                  background: filterKind === opt.v
                    ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                    : 'transparent',
                  color: filterKind === opt.v ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 280, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ClassSectionFilters
            items={visibleFolders}
            itemNoun="folders"
            gradeFilter={gradeFilter}
            sectionFilter={sectionFilter}
            subjectFilter={subjectPick}
            assignmentWord={assignmentWord}
            onAssignmentWord={setAssignmentWord}
            onGrade={(grade) => {
              setSubjectPick(null);
              setSectionFilter(null);
              if (gradeFilter === grade) {
                setGradeFilter(null);
              } else {
                setGradeFilter(grade);
                setSelectedDate('');
                setSelectedEndDate('');
                setFilterMode('none');
              }
            }}
            onSection={(section) => {
              const next = sectionFilter === section ? null : section;
              setSectionFilter(next);
              if (subjectPick && !itemsInClass(visibleFolders, gradeFilter, next).some((folder) => (folder.subject || '').trim() === subjectPick)) {
                setSubjectPick(null);
              }
            }}
          />
        {(filterMode !== 'none' || gradeFilter) && (
          <>
          {!loading && filteredFolders.length === 0 && (
            <div className="no-folders">
              <p>{gradeFilter ? 'No folders for this class.' : 'No folders found. Folders are automatically created when rubrics are saved.'}</p>
            </div>
          )}

          {!loading && filteredFolders.length > 0 && (
            <div className="folders-grid">
          {filteredFolders.map(folder => {
            // Status priority mirrors the Evaluate tab so the same folder
            // looks the same wherever it appears: printed > done > empty.
            const isPrinted = !!folder.printed_at;
            const isDone = !isPrinted && !!folder.completed_at;
            const isEmpty = !isPrinted && !isDone && folder.scan_count === 0;
            const statusClass = isPrinted ? 'printed' : isDone ? 'completed' : isEmpty ? 'empty' : '';

            // Build the on-disk folder name the watcher would have created
            // (same algorithm as folder-server.js autoCreateMissingLocalFolders:
            // "<displayTitle> <Mon DD, YYYY>"). If that name exists in the
            // set returned by the local /local-folders endpoint, the folder
            // is mirrored on the user's G Drive and we show a purple badge.
            const localName = buildLocalFolderName(folder);
            const inGDrive = !!localName && localFolderNames.has(localName.toLowerCase());

            return (
              <div
                key={folder.id}
                className={`folder-card ${statusClass}`}
                onClick={() => handleFolderClick(folder)}
              >
                <div className="folder-icon">
                  <FontAwesomeIcon icon={faFolder} />
                </div>
                <div className="folder-info">
                  <h3 className="folder-title">
                    <span className="folder-title-text">
                      {folderDisplayTitle(folder)} {formatDate(folder.test_date)}
                    </span>
                    <span
                      className={`folder-image-count${folder.scan_count === 0 ? ' folder-image-count--empty' : ''}`}
                      title={`${folder.scan_count} image${folder.scan_count !== 1 ? 's' : ''} in this folder`}
                    >
                      {folder.scan_count} image{folder.scan_count !== 1 ? 's' : ''}
                    </span>
                    {isPrinted && <span className="printed-badge">🖨 Printed</span>}
                    {isDone && <span className="done-badge">🖨 Ready to Print</span>}
                    {isEmpty && <span className="empty-badge">⚠ Empty</span>}
                  </h3>
                  {folder.assignment_name && (
                    <div
                      className="folder-assignment"
                      title={`Assignment: ${folder.assignment_name}`}
                    >
                      <i className="fa-solid fa-tag" style={{ fontSize: '0.66rem', flexShrink: 0 }}></i>
                      <span>{folder.assignment_name}</span>
                    </div>
                  )}
                  <div className="folder-meta">
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faUser} />
                      <span>{folder.created_by}</span>
                    </div>
                    {(() => {
                      const stepInfo = autoCheckStepsMap.get(String(folder.id)) || {};
                      const steps = stepInfo.steps || {};
                      const assessMeta = assessSnapshotMetaMap.get(String(folder.id));
                      const runAt = stepInfo.run_at || (steps.run && steps.run.at) || null;
                      const saveAt = (steps.save && steps.save.at) || null;
                      const checkedAt = (assessMeta && assessMeta.saved_at) || saveAt || runAt;
                      const snapSt = snapshotFolderStatusMap.get(String(folder.id));
                      const approvedAt = (steps.approve && steps.approve.status === 'done' && steps.approve.at)
                        || ((snapSt === 'copy_to_edupage' || snapSt === 'completed' || isPrinted || isDone)
                          && (folder.completed_at || folder.printed_at))
                        || null;
                      const gradingRunning = !!(steps.run && steps.run.status === 'running');
                      const checkedLabel = gradingRunning ? 'Grading started' : 'Last checked';
                      const checkedValue = gradingRunning ? (runAt || checkedAt) : checkedAt;
                      const checkedStr = formatFolderPkt(checkedValue);
                      const approvedStr = formatFolderPkt(approvedAt);
                      if (!checkedStr && !approvedStr) return null;
                      return (
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {checkedStr && (
                            <div title="Last time this folder was graded — same clock as Last Assessed on the Check tab" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <i className="fa-solid fa-clock" style={{ width: 12, color: '#059669' }}></i>
                              <span style={{ whiteSpace: 'nowrap' }}>{checkedLabel} {checkedStr}</span>
                            </div>
                          )}
                          {approvedStr && (
                            <div title="Last time this folder was approved into the student list" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <i className="fa-solid fa-circle-check" style={{ width: 12, color: '#2563eb' }}></i>
                              <span style={{ whiteSpace: 'nowrap' }}>Last approved {approvedStr}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {inGDrive && (
                      <span
                        title={`Folder also exists locally as "${localName}"`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 6,
                          padding: '2px 10px',
                          borderRadius: 999,
                          background: 'rgba(139, 92, 246, 0.15)',
                          border: '1px solid rgba(139, 92, 246, 0.45)',
                          color: '#6D28D9',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: 0.2,
                          width: 'fit-content'
                        }}
                      >
                        in G Drive
                      </span>
                    )}
                  </div>
                  <ScanErrorFlags
                    folder={folder}
                    unmatchedLabelMap={unmatchedLabelMap}
                    ocrNoiseMap={ocrNoiseMap}
                    unexpectedBlankMap={unexpectedBlankMap}
                    overMaxMarksMap={overMaxMarksMap}
                    thinkingMarksMap={thinkingMarksMap}
                    checkErrorMap={checkErrorMap}
                    snapshotStatus={snapshotFolderStatusMap.get(String(folder.id)) || ''}
                    approveError={String(((autoCheckStepsMap.get(String(folder.id)) || {}).steps || {}).approve?.error || '')}
                    onShow={showErrorHover}
                    onHide={hideErrorHover}
                  />
                  {/* Upload — pushes just THIS folder's local images to the
                      server on demand (same as the Sort/Evaluate tab button).
                      No other folder is touched. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {(() => {
                    const isUploading = uploadingFolderIds.has(folder.id);
                    const isUploaded = uploadedFolderIds.has(folder.id);
                    // blue = ready, grey = in progress, green = done.
                    // Uploaded renders as a transparent (outline) button.
                    const color = isUploading ? '#6b7280' : isUploaded ? '#059669' : '#2563eb';
                    const bg = 'transparent';
                    const fg = color;
                    const label = isUploading ? 'Uploading…' : isUploaded ? 'Uploaded' : 'Upload';
                    const icon = isUploading ? 'fa-solid fa-spinner fa-spin'
                      : isUploaded ? 'fa-solid fa-circle-check'
                      : 'fa-solid fa-image';
                    return (
                      <button
                        onClick={(e) => uploadFolderImages(e, folder)}
                        disabled={isUploading}
                        title={isUploaded
                          ? `Uploaded — click to upload again if you added more images (${folder.scan_count} on the server)`
                          : `Upload this folder's images to the server now (${folder.scan_count} currently on the server)`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '4px 12px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          border: `1px solid ${color}`,
                          borderRadius: 6,
                          background: bg,
                          color: fg,
                          cursor: isUploading ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                          width: 'fit-content'
                        }}
                      >
                        <i className={icon}></i>
                        {label}
                      </button>
                    );
                  })()}
                  {(() => {
                    const verified = !!(folderReviews.verified && folderReviews.verified[String(folder.id)]);
                    const requests = (folderReviews.requests && folderReviews.requests[String(folder.id)]) || [];
                    const sheetMeta = assessSnapshotMetaMap.get(String(folder.id));
                    const sheetStudents = sheetMeta ? Number(sheetMeta.record_count) || 0 : null;
                    const sheetCount = sheetStudents == null
                      ? 'No saved result sheet.'
                      : `${sheetStudents} student${sheetStudents === 1 ? '' : 's'} in the result sheet.`;
                    return (
                      <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReportFolder(folder);
                    }}
                    title={verified
                      ? `${sheetCount} Result is marked OK. Open the result sheet.`
                      : `${sheetCount} Open the result sheet. Stays orange until Result is OK is clicked.`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 12px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      border: `1px solid ${verified ? '#15803d' : '#c2410c'}`,
                      borderRadius: 6,
                      background: verified ? '#16a34a' : '#ea580c',
                      color: '#fff',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      width: 'fit-content'
                    }}
                  >
                    <i className="fa-solid fa-table"></i>
                    Result
                  </button>
                  <RegradeChip
                    requests={requests}
                    folderPath={`rubrics/${folder.id}`}
                    onOpen={(e) => openRegradeFromCard(e, folder)}
                  />
                      </>
                    );
                  })()}
                  </div>
                </div>
              </div>
            );
          })}
            </div>
          )}
          </>
        )}
        </div>
        <SubjectColumn
          items={visibleFolders}
          gradeFilter={gradeFilter}
          sectionFilter={sectionFilter}
          subjectFilter={subjectPick}
          onSubject={(subject) => setSubjectPick(subjectPick === subject ? null : subject)}
        />
        </div>
      </div>

      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />
      {reportFolder && (
        <QuestionReportSheet
          folder={reportFolder}
          title={folderDisplayTitle(reportFolder)}
          onClose={() => setReportFolder(null)}
          onSwitchFolder={setReportFolder}
          onReviewChange={(review) => applyFolderReview(reportFolder.id, review)}
        />
      )}
      {errorHover && errorHover.rows.length > 0 && createPortal(
        <div
          onMouseEnter={() => showErrorHover(errorHover)}
          onMouseLeave={hideErrorHover}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: errorHover.top,
            left: errorHover.left,
            zIndex: 10000,
            background: '#fff',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '6px 8px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            minWidth: 180,
            maxWidth: 320,
            maxHeight: 220,
            overflowY: 'auto',
            color: '#7F1D1D',
            fontSize: '0.72rem',
            fontWeight: 700,
            lineHeight: 1.45
          }}
        >
          {errorHover.rows.map((row, index) => (
            <div key={`${row.name}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{row.name}</span>
              {row.detail ? (
                <span style={{ fontWeight: 600, color: '#B91C1C', textAlign: 'right' }}>{row.detail}</span>
              ) : null}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function flagRows(list, detail) {
  return (Array.isArray(list) ? list : []).map((student) => ({
    name: student.name || 'Unknown',
    detail: detail(student)
  }));
}

function ScanErrorFlags({
  folder,
  unmatchedLabelMap,
  ocrNoiseMap,
  unexpectedBlankMap,
  overMaxMarksMap,
  thinkingMarksMap,
  checkErrorMap,
  snapshotStatus,
  approveError,
  onShow,
  onHide
}) {
  const id = String(folder.id);
  const flags = [];

  const unmatched = unmatchedLabelMap.get(id);
  const unmatchedStudents = unmatched && Array.isArray(unmatched.students) ? unmatched.students : [];
  if (unmatchedStudents.length) {
    const oneLabel = unmatchedStudents.length === 1
      && Array.isArray(unmatchedStudents[0].labels)
      && unmatchedStudents[0].labels.length === 1
      ? unmatchedStudents[0].labels[0]
      : '';
    flags.push({
      key: 'unmatched',
      tone: 'red',
      label: oneLabel
        ? `Unmatched label · ${oneLabel}`
        : `Unmatched label${unmatchedStudents.length > 1 ? ` (${unmatchedStudents.length})` : ''}`,
      rows: flagRows(unmatchedStudents, (student) => (Array.isArray(student.labels) ? student.labels : []).join(', '))
    });
  }

  const noise = ocrNoiseMap.get(id);
  const noiseStudents = noise && Array.isArray(noise.students) ? noise.students : [];
  const badStudents = noiseStudents
    .map((student) => ({
      ...student,
      notes: (Array.isArray(student.notes) ? student.notes : []).filter((note) => !isSuspectedNote(note))
    }))
    .filter((student) => student.notes.length);
  const chatterRows = [];
  for (const student of noiseStudents) {
    for (const page of suspectedPagesFromNotes(student.notes)) {
      chatterRows.push({ name: student.name, detail: page.label, kind: page.kind });
    }
  }
  if (chatterRows.length) {
    const thinkingOnly = chatterRows.every((row) => row.kind === 'thinking');
    const count = new Set(chatterRows.map((row) => row.name)).size;
    flags.push({
      key: 'chatter',
      tone: 'amber',
      label: thinkingOnly
        ? (count > 1 ? `Thinking Text (${count})` : 'Thinking Text')
        : (count > 1 ? `Suspected Chatter (${count})` : 'Suspected Chatter'),
      rows: chatterRows.map((row) => ({ name: row.name, detail: row.detail }))
    });
  }
  if (badStudents.length) {
    flags.push({
      key: 'ocr',
      tone: 'red',
      label: badStudents.length > 1 ? `Bad OCR (${badStudents.length})` : 'Bad OCR',
      rows: flagRows(badStudents, (student) => (Array.isArray(student.notes) ? student.notes : []).join(', '))
    });
  }

  const blank = unexpectedBlankMap.get(id);
  const blankStudents = blank && Array.isArray(blank.students) ? blank.students : [];
  if (blankStudents.length) {
    flags.push({
      key: 'blank',
      tone: 'red',
      label: blankStudents.length > 1 ? `Unexpected blank page (${blankStudents.length})` : 'Unexpected blank page',
      rows: flagRows(blankStudents, (student) => (Array.isArray(student.pages) ? student.pages : []).join(', '))
    });
  }

  const over = overMaxMarksMap.get(id);
  const overStudents = over && Array.isArray(over.students) ? over.students : [];
  if (overStudents.length) {
    flags.push({
      key: 'over',
      tone: 'red',
      label: `Marks > max${overStudents.length > 1 ? ` (${overStudents.length})` : ''}`,
      rows: flagRows(overStudents, (student) => (Array.isArray(student.scores) ? student.scores : []).join(', '))
    });
  }

  const thinking = (thinkingMarksMap || new Map()).get(id);
  const thinkingStudents = thinking && Array.isArray(thinking.students) ? thinking.students : [];
  if (thinkingStudents.length) {
    flags.push({
      key: 'thinking',
      tone: 'red',
      label: thinkingStudents.length > 1 ? `Thinking in marks (${thinkingStudents.length})` : 'Thinking in marks',
      rows: flagRows(thinkingStudents, (student) => (Array.isArray(student.phrases) ? student.phrases : []).join(', '))
    });
  }

  const missing = Array.isArray(folder.ocr_missing) ? folder.ocr_missing : [];
  if (missing.length) {
    flags.push({
      key: 'missing',
      tone: 'orange',
      label: 'ocr missing',
      rows: missing.map((item) => ({
        name: item.student || 'Unknown',
        detail: formatMissingImageNumbers(item.pages)
      }))
    });
  }

  const failedStudents = (checkErrorMap.get(id) || {}).students || [];
  if (snapshotStatus === 'error' && /ungraded/i.test(approveError) && failedStudents.length) {
    flags.push({
      key: 'ungraded',
      tone: 'ungraded',
      label: `⚠ Ungraded rows (${failedStudents.length})`,
      rows: flagRows(failedStudents, (student) => (Array.isArray(student.errors) ? student.errors : []).join(', '))
    });
  }

  if (!flags.length) return null;

  const chip = (tone) => {
    if (tone === 'ungraded') {
      return {
        padding: '2px 7px',
        borderRadius: 10,
        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
        border: 'none',
        color: 'white',
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        cursor: 'default',
        whiteSpace: 'nowrap'
      };
    }
    if (tone === 'amber') {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 6px',
        borderRadius: 6,
        background: 'rgba(245, 158, 11, 0.15)',
        border: '1px solid #D97706',
        color: '#B45309',
        fontSize: '0.68rem',
        fontWeight: 800,
        cursor: 'default',
        whiteSpace: 'nowrap'
      };
    }
    if (tone === 'orange') {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 7px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 700,
        background: '#fff7ed',
        color: '#c2410c',
        border: '1px solid #fdba74',
        cursor: 'default',
        whiteSpace: 'nowrap'
      };
    }
    return {
      padding: '3px 7px',
      borderRadius: 6,
      background: 'rgba(220, 38, 38, 0.1)',
      border: '1px solid #DC2626',
      color: '#991B1B',
      fontSize: '0.68rem',
      fontWeight: 800,
      lineHeight: 1.35,
      cursor: 'default',
      whiteSpace: 'nowrap'
    };
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}
    >
      {flags.map((flag) => (
        <div
          key={flag.key}
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const width = 300;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            onShow({ key: `${id}:${flag.key}`, top: rect.bottom + 4, left, rows: flag.rows });
          }}
          onMouseLeave={onHide}
          style={chip(flag.tone)}
        >
          {flag.label}
        </div>
      ))}
    </div>
  );
}
